/**
 * Faturalar modülü — Firestore yol sabitleri ve şube eşlemesi.
 */

export const BRANCH_BANKING_ID = {
  sefakoy_merkez: 'sefakoy-merkez',
  sefakoy_sube: 'sefakoy-sube',
  kavakli_sube: 'kavakli'
};

/** Uygulama şube id (alt çizgili) → banka Firestore id (tireli). */
export const resolveBankingId = (appSube) =>
  BRANCH_BANKING_ID[String(appSube || '').trim()] || String(appSube || '').trim();

export const KNOWN_BRANCHES = [
  { key: 'sefakoy_merkez', label: 'Sefaköy Merkez' },
  { key: 'sefakoy_sube', label: 'Sefaköy Şube' },
  { key: 'kavakli_sube', label: 'Kavaklı Şube' }
];

export const branchLabel = (key) =>
  KNOWN_BRANCHES.find((x) => x.key === key)?.label || String(key || '').replace(/_/g, ' ');

export const INVOICE_READ_LIMITS = {
  bankMovements: 500
};

/** Faturalar eşleştirme listelerinde tüm kayıtları göster (varsayılan: filtreli). */
export const INVOICE_LIST_SHOW_ALL = false;

/** Fatura kesmeye uygun işlem türleri. */
export const INVOICE_ELIGIBLE_ISLEM_TURU = ['Satış', 'Giriş'];

/** Faturalar kuyruğunda sayfa başına kayıt. */
export const INVOICE_LIST_PAGE_SIZE = 15;

/** Giden / Gelen belgeler listelerinde sayfa başına kayıt. */
export const INVOICE_DOCS_PAGE_SIZE = 15;

/** Faturalandırılacak listesi — satırlar iki satırlık olduğu için daha az sığıyor. */
export const INVOICE_TO_INVOICE_PAGE_SIZE = 11;

export const INVOICE_DATE_FILTER = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  LAST_7_DAYS: 'last_7_days',
  LAST_15_DAYS: 'last_15_days',
  THIS_WEEK: 'this_week',
  THIS_MONTH: 'this_month',
  ALL: 'all'
};

export const INVOICE_DATE_FILTER_DEFAULT = INVOICE_DATE_FILTER.TODAY;

export const INVOICE_DATE_FILTER_OPTIONS = [
  { value: INVOICE_DATE_FILTER.TODAY, label: 'Bugün' },
  { value: INVOICE_DATE_FILTER.YESTERDAY, label: 'Dün' },
  { value: INVOICE_DATE_FILTER.LAST_7_DAYS, label: '7 Gün' },
  { value: INVOICE_DATE_FILTER.LAST_15_DAYS, label: '15 Gün' },
  { value: INVOICE_DATE_FILTER.THIS_WEEK, label: 'Bu Hafta' },
  { value: INVOICE_DATE_FILTER.THIS_MONTH, label: 'Bu Ay' },
  { value: INVOICE_DATE_FILTER.ALL, label: 'Tümü' }
];

/** Onay kuyruğu üst tarih filtresi */
export const QUEUE_DATE_FILTER_DEFAULT = INVOICE_DATE_FILTER.ALL;

/** Kuyrukta dört seçenek yeter; ara aralıklar günlük işte kullanılmıyordu. */
export const QUEUE_DATE_FILTER_OPTIONS = [
  { value: INVOICE_DATE_FILTER.TODAY, label: 'Bugün' },
  { value: INVOICE_DATE_FILTER.LAST_7_DAYS, label: '7 Gün' },
  { value: INVOICE_DATE_FILTER.THIS_MONTH, label: 'Bu Ay' },
  { value: INVOICE_DATE_FILTER.ALL, label: 'Tümü' }
];

/** Kuyruğun üst sırasında görünen durum filtreleri (günlük iş). */
export const QUEUE_PRIMARY_STATUS_FILTERS = ['pendingApproval', 'missing', 'sendReady', 'all'];

/** Taşma menüsündeki durum filtreleri (inceleme / arşiv). */
export const QUEUE_SECONDARY_STATUS_FILTERS = ['sent', 'error', 'cancelled'];

export const faturaEslestirmeleriCollectionPath = (sube) =>
  `subeler/${sube}/faturaEslestirmeleri`;

/** Client-side skor etiketleri (FAZ 1). */
export const INVOICE_MATCH_TYPE = {
  SINGLE: 'single',
  MULTI: 'multi',
  /** Banka/işlem eşleşmesi olmadan sıfırdan fatura */
  STANDALONE: 'standalone'
};

export const INVOICE_MATCH_STATUS = {
  MATCHED: 'matched',
  SUSPICIOUS: 'suspicious',
  UNMATCHED: 'unmatched',
  MANUAL: 'manual'
};

/** Firestore'a yazılan eşleşme durumu. */
export const INVOICE_PERSISTED_MATCH_STATUS = {
  MATCHED: 'matched',
  SUSPICIOUS: 'suspicious',
  MANUAL: 'manual'
};

export const INVOICE_STATUS = {
  DRAFT: 'draft',
  READY: 'ready',
  CANCELLED: 'cancelled',
  ERROR: 'error',
  SENDING: 'sending',
  SENT: 'sent',
  APPROVED: 'approved'
};

/** Mükerrer kontrolünde aktif sayılan durumlar */
export const ACTIVE_INVOICE_STATUSES = [
  INVOICE_STATUS.DRAFT,
  INVOICE_STATUS.READY,
  INVOICE_STATUS.SENDING,
  INVOICE_STATUS.SENT,
  INVOICE_STATUS.APPROVED,
  INVOICE_STATUS.ERROR
];

export const CUSTOMER_STATUS = {
  COMPLETE: 'complete',
  MISSING: 'missing'
};

export const DRAFT_FILTER = {
  ALL: 'all',
  /** Onaya gönderilebilir (müşteri+matrah tamam, draft) */
  PENDING_APPROVAL: 'pendingApproval',
  MISSING: 'missing',
  DRAFT: 'draft',
  SEND_READY: 'sendReady',
  /** GİB’e iletilmiş (gönderildi + GİB onaylı) */
  SENT: 'sent',
  CANCELLED: 'cancelled',
  ERROR: 'error'
};

export const INVOICE_VIEW = {
  /** Onay kuyruğu (varsayılan) */
  QUEUE: 'onay',
  /** Manuel inceleme / düşük skor eşleştirme */
  MATCH: 'eslestirme',
  /** @deprecated QUEUE ile aynı — geriye uyum */
  DRAFTS: 'taslaklar',
  SETTINGS: 'ayarlar'
};

/** Faturalar V2 navigasyon (sidebar) */
export const INVOICE_NAV = {
  OVERVIEW: 'overview',
  NEW_INVOICE: 'new_invoice',
  APPROVAL: 'approval',
  TO_INVOICE: 'to_invoice',
  /** Giden Faturalar: Kolaysoft arşivi + EDM ile gönderilenler */
  OUTBOUND: 'outbound',
  NEEDS_ACTION: 'needs_action',
  INBOUND: 'inbound',
  /** Gider Pusulasi: musteriden (fatura kesemeyen kisiden) yapilan alimlar */
  EXPENSE_NOTE: 'expense_note',
  REPORTS: 'reports',
  SETTINGS: 'settings',
  AML: 'aml',
  QUICK_LOOKUP: 'quick_lookup',
  /** Eski "Kolaysoft Arşivi" hedefi → Giden Faturalar */
  KOLAYSOFT_ARCHIVE: 'kolaysoft_archive'
};

/** UI ana durum etiketleri (DB alanlarından türetilir) */
export const INVOICE_UI_STATUS = {
  DRAFT: 'draft',
  AWAITING_APPROVAL: 'awaiting_approval',
  MISSING_INFO: 'missing_info',
  APPROVED: 'approved',
  NEEDS_ACTION: 'needs_action',
  CANCELLED: 'cancelled'
};

export const INVOICE_SETTINGS_DOC_ID = 'current';

/**
 * Kimlik bilgisi sunucuda (Secret Manager) tutulan, adresi sabit sağlayıcılar.
 * Bunlarda ekranda API URL / key / secret alanı gösterilmez, şifre girilmez.
 * EDM bırakıldı ama eski kayıtlarda `provider: 'edm'` kalmış olabilir.
 */
export const SERVER_MANAGED_PROVIDERS = ['kolaysoft', 'edm'];
export const isServerManagedProvider = (provider) =>
  SERVER_MANAGED_PROVIDERS.includes(String(provider || '').toLowerCase());

export const INVOICE_PROVIDER_OPTIONS = [
  { value: 'none', label: 'Seçilmedi' },
  { value: 'kolaysoft', label: 'Kolaysoft' },
  { value: 'uyumsoft', label: 'Uyumsoft' },
  { value: 'edm', label: 'EDM' },
  { value: 'foriba', label: 'Foriba' },
  { value: 'other', label: 'Diğer' }
];

/** Tüm şubelerde aynı tutulan firma alanları */
export const SHARED_COMPANY_SETTINGS_FIELDS = [
  'companyTitle',
  'taxNumber',
  'taxOffice',
  'mersisNo',
  'address',
  'city',
  'district',
  'phone',
  'email',
  'companyCode'
];

/** UKBA iç akış vs entegratör hazırlığı — UI banner / Genel Bakış */
export const resolveInvoiceIntegrationReadiness = (settings = {}) => {
  const provider = String(settings?.provider || 'none').toLowerCase();
  const enabled = settings?.enabled === true;
  const testMode = settings?.testMode !== false;
  const hasCompany = Boolean(String(settings?.companyTitle || '').trim() && String(settings?.taxNumber || '').trim());

  if (provider === 'none' || !enabled) {
    return {
      key: 'ukba_only',
      severity: 'info',
      title: 'UKBA fatura akışı hazır',
      detail: 'Eşleş → taslak → onay. GİB gönderimi entegratör sonrası.',
      gibReady: false,
      hasCompany
    };
  }
  if (isServerManagedProvider(provider) && enabled) {
    const ad =
      INVOICE_PROVIDER_OPTIONS.find((o) => o.value === String(provider || '').toLowerCase())?.label ||
      'Entegratör';
    return testMode
      ? {
          key: 'entegrator_test',
          severity: 'warning',
          title: `${ad} test ortamı`,
          detail: 'Gönderimler test ortamına gider, GİB’e ulaşmaz.',
          gibReady: true,
          hasCompany
        }
      : {
          key: 'entegrator_live',
          severity: 'success',
          title: `${ad} canlı`,
          detail: 'GİB’e Gönder canlı ortama iletir.',
          gibReady: true,
          hasCompany
        };
  }
  return {
    key: 'provider_pending',
    severity: 'info',
    title: 'Sağlayıcı seçili',
    detail: 'Kurulum tamamlanmalı.',
    gibReady: enabled,
    hasCompany
  };
};

/** EDM mailinde istenecekler (UI’da liste basılmaz) */
export const EDM_SETUP_CHECKLIST = [
  'Firma ünvanı, VKN, vergi dairesi',
  'EDM kullanıcı adı ve şifre',
  'Test ortamı erişim onayı',
  'Canlı ortam açılış onayı',
  'Gönderici birim / GB alias (gerekirse)',
  'e-Fatura / e-Arşiv yetki durumu'
];

export const DEFAULT_INVOICE_SETTINGS = {
  provider: 'none',
  providerName: '',
  enabled: false,
  testMode: true,
  /** not_configured | credentials_pending | connected — canlı API bağlanınca güncellenir */
  providerConnectionStatus: 'not_configured',
  companyTitle: '',
  taxNumber: '',
  taxOffice: '',
  mersisNo: '',
  address: '',
  city: '',
  district: '',
  phone: '',
  email: '',
  apiBaseUrl: '',
  apiUsername: '',
  /** Hassas alanlar client Firestore'a yazılmaz; Secret Manager'a taşınacak */
  apiPasswordSet: false,
  apiKey: '',
  apiSecretSet: false,
  companyCode: '',
  branchCode: '',
  defaultMatrahType: 'Özel Matrah',
  defaultPaymentType: 'Havale/EFT',
  defaultCurrency: 'TRY',
  /**
   * Fatura şartları — tek tip liste (bkz. utils/invoiceRules.js).
   * Boş liste = şart yok; o zaman kategori tablosuna düşülür.
   */
  invoiceRules: [],
  /** GİB seri numarası 3 harftir; fazlası sessizce kırpılır. */
  /** Alıcı kimliği zorunlu olan tutar eşiği (TL). Altında nihai tüketici kesilebilir. */
  kimlikZorunluEsikTl: 12000,
  invoicePrefix: 'UKB',
  nextInvoiceNo: 1,
  /**
   * Eski yedek: katalog satırı yoksa işçilik % (KDV dahil pay).
   * Asıl hesap kategori detay tablosundan (productCatalog) yapılır.
   */
  workmanshipRates: {
    'Takı Ürün': 12,
    Sarrafiye: 8,
    'Gram Altın': 5,
    default: 10
  },
  /** Kategori detayları — ürün → has çarpanı / milyem / 8 AYAR eşlemesi */
  productCatalog: null,
  /** Boş bırakılırsa has fiyatı × milyem ile hesaplanır */
  price22AyarTakiPerGram: 0,
  milyem22Ayar: 0.916,
  iscilik22AyarPerGram: 0,
  /**
   * Altın Tamir Ücreti faturasında işçilik satırının birim fiyatı (KDV hariç).
   * Tamir bir hizmet; has hesabı yapılmaz, işçilik her faturada aynıdır.
   */
  tamirIscilikNetTl: 100,
  /**
   * ÜRÜNÜ DEĞİŞTİRİLEN faturada işçiliğe ayrılan pay (%, KDV dahil).
   *
   * Ürün 8/14/22 ayara çevrildiğinde gram tutardan hesaplanıyor; bu da altın
   * değerini tutarın tamamına eşitleyip işçiliği sıfırlıyordu (3.500 TL'lik
   * faturada 55 kuruş KDV). Artık tutarın bu kadarı işçiliğe ayrılır, gram
   * kalan altın payından hesaplanır.
   *
   * İşlemdeki ürün olduğu gibi kesiliyorsa BU ORAN UYGULANMAZ — orada
   * işçilik satışın gerçek kârıdır.
   */
  cevrilenUrunIscilikYuzdesi: {
    '8taki': 10,
    '14taki': 10,
    '22taki': 1,
    '22bilezik': 1
  },
  /** Giriş: bu tutarın altı → Altın Tamir Ücreti (eski alan; invoiceConditionRules tercih edilir) */
  girisTamirMaxTl: 3000,
  /** Giriş: bu tutar ve üzeri → 24 Ayar Gram Altın (eski alan) */
  girisGramAltinMinTl: 3000,
  /** Eklenebilir şartlar — tutar eşiği → fatura ürün tipi */
  invoiceConditionRules: null,
  /**
   * Kısmi banka ödemelerinde tutara en yakın ürünü seç.
   * Ayrıntı: src/utils/invoiceAmountProductRule.js
   */
  amountNearestProductRule: null,
  /** Kredi kartı satışı faturası (kart tutarı eşiği aşarsa) — bkz. invoiceKrediKartiRule.js */
  krediKartiRule: null,
  /** Otomasyon — güvenli varsayılan: eşleştir + taslak; onay/gönder manuel */
  automationEnabled: true,
  automaticMatching: true,
  automaticDraft: true,
  automaticApproval: false,
  automaticSend: false,
  runMatchOnOpen: true,
  lowConfidenceRequiresManual: true
};

export const INVOICE_MATCH_THRESHOLDS = {
  matched: 80,
  suspicious: 50
};

export const MATCH_CONFIDENCE = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low'
};

/** Skor → açıklanabilir güven (sahte % göstermeyiz) */
export const resolveMatchConfidence = (score) => {
  const n = Number(score) || 0;
  if (n >= INVOICE_MATCH_THRESHOLDS.matched) return MATCH_CONFIDENCE.HIGH;
  if (n >= INVOICE_MATCH_THRESHOLDS.suspicious) return MATCH_CONFIDENCE.MEDIUM;
  return MATCH_CONFIDENCE.LOW;
};

export const matchConfidenceLabel = (confidence) => {
  switch (confidence) {
    case MATCH_CONFIDENCE.HIGH:
      return 'Yüksek';
    case MATCH_CONFIDENCE.MEDIUM:
      return 'Orta';
    case MATCH_CONFIDENCE.LOW:
    default:
      return 'Düşük';
  }
};

export const INVOICE_AUDIT_TYPES = {
  DRAFT_CREATED: 'draft_created',
  DRAFT_UPDATED: 'draft_updated',
  APPROVED: 'approved',
  REVERTED: 'reverted',
  CANCELLED: 'cancelled',
  AUTO_MATCHED: 'auto_matched',
  MANUAL_MATCHED: 'manual_matched',
  SENT: 'sent',
  SEND_ERROR: 'send_error'
};

/** Firestore skor → kalıcı matchStatus */
export const toPersistedMatchStatus = (score) => {
  if (score >= INVOICE_MATCH_THRESHOLDS.matched) return INVOICE_PERSISTED_MATCH_STATUS.MATCHED;
  if (score >= INVOICE_MATCH_THRESHOLDS.suspicious) return INVOICE_PERSISTED_MATCH_STATUS.SUSPICIOUS;
  return INVOICE_PERSISTED_MATCH_STATUS.MANUAL;
};
